/**
 * Trusscore Estimator - BOM Smoothing
 *
 * Consolidates low-quantity "orphan" panel SKUs into higher-quantity SKUs
 * of the same color to reduce SKU proliferation on orders.
 *
 * Design principle: Trade small amounts of additional waste for simpler BOMs
 * that are easier to order and manage.
 *
 * ARCHITECTURE: Smoothing operates on WallResult[] + CeilingResult BEFORE
 * surface breakdowns and BOM are built. This ensures both the Product
 * Placement Guide and BOM reflect the smoothed panels.
 *
 * Example:
 * Before: 27x TC1608 (8'), 1x TC1620 (20'), 1x TC1610 (10')
 * After:  30x TC1608 (8') — orphans absorbed into high-volume SKU
 */

import {
  BillOfMaterials,
  BOMSmoothingConfig,
  BOMSmoothingResult,
  BOMSmoothingSummary,
  MaterialItem,
  ProductColor,
  SKUSubstitution,
  CoreDimensions,
} from '../types';
import {
  DEFAULT_ORPHAN_THRESHOLD,
  DEFAULT_MAX_WASTE_INCREASE_PERCENT,
} from './constants';
import { getWCBProduct } from '../data/productTable';
import { CeilingResult } from './ceiling';
import { WallResult } from './walls';

// ============================================================================
// PRE-BOM SMOOTHING (operates on WallResult[] + CeilingResult)
// ============================================================================

/**
 * Tracks where each panel SKU is used, so we can modify it in place.
 */
interface PanelOccurrence {
  source: 'wall' | 'ceiling';
  sourceIndex: number;      // Wall index, or 0 for ceiling
  panelIndex: number;       // Index in the panels[] array
  item: MaterialItem;       // Reference to the panel
  cutLength: number;        // Actual cut length this panel provides
  totalCutsNeeded: number;  // Total individual cuts this entry provides
  isVertical: boolean;      // Whether this is a vertical cut panel
}

/**
 * Result of smoothing WallResult[] and CeilingResult.
 */
export interface SmoothResultsOutput {
  wallResults: WallResult[];
  ceilingResult: CeilingResult | null;
  substitutions: SKUSubstitution[];
  summary: BOMSmoothingSummary;
}

/**
 * Smooth panel selections across all walls and ceiling BEFORE building
 * surface breakdowns and BOM.
 *
 * This ensures both the Product Placement Guide (per-surface) and the
 * consolidated BOM reflect the smoothed panels.
 *
 * Algorithm:
 * 1. Collect all W&CB panels from all walls and ceiling
 * 2. Calculate global quantities per productID to identify orphans vs popular SKUs
 * 3. For each orphan occurrence, find a popular panel of the same color
 *    that can serve the same cuts within waste tolerance
 * 4. Replace orphan entries in the WallResult/CeilingResult panels arrays
 *
 * @param wallResults - Array of wall calculation results
 * @param ceilingResult - Ceiling calculation result (may be null)
 * @param config - Smoothing configuration
 * @returns Modified results with smoothed panels
 */
export function smoothResults(
  wallResults: WallResult[],
  ceilingResult: CeilingResult | null,
  config: BOMSmoothingConfig
): SmoothResultsOutput {
  if (!config.enabled) {
    const totalSKUs = countUniqueSKUs(wallResults, ceilingResult);
    return {
      wallResults,
      ceilingResult,
      substitutions: [],
      summary: createEmptySummary(totalSKUs),
    };
  }

  const threshold = config.orphanThreshold ?? DEFAULT_ORPHAN_THRESHOLD;
  const maxWaste = config.maxWasteIncreasePercent ?? DEFAULT_MAX_WASTE_INCREASE_PERCENT;

  // Step 1: Deep-clone results so we don't mutate originals
  const newWallResults = wallResults.map(wr => ({
    ...wr,
    panels: wr.panels.map(p => ({ ...p })),
  }));
  const newCeilingResult = ceilingResult ? {
    ...ceilingResult,
    panels: ceilingResult.panels.map(p => ({ ...p })),
  } : null;

  // Step 2: Collect all W&CB panel occurrences with cut-length metadata
  const occurrences = collectPanelOccurrences(newWallResults, newCeilingResult);

  // Step 3: Calculate global quantities per productID
  const globalQty = new Map<string, number>();
  for (const occ of occurrences) {
    globalQty.set(
      occ.item.productID,
      (globalQty.get(occ.item.productID) || 0) + occ.item.quantity
    );
  }

  // Step 4: Classify orphans vs popular, grouped by color
  const orphanIDs = new Set<string>();
  const popularByColor = new Map<ProductColor, PopularPanel[]>();

  for (const [productID, qty] of globalQty) {
    const sample = occurrences.find(o => o.item.productID === productID)!.item;

    if (qty <= threshold) {
      orphanIDs.add(productID);
    } else {
      const color = sample.color;
      if (!popularByColor.has(color)) {
        popularByColor.set(color, []);
      }
      popularByColor.get(color)!.push({
        productID,
        lengthFeet: sample.lengthFeet,
        color,
        globalQty: qty,
      });
    }
  }

  // Sort popular panels by quantity descending (prefer highest-volume substitutes)
  for (const [, panels] of popularByColor) {
    panels.sort((a, b) => b.globalQty - a.globalQty);
  }

  // Step 5: No orphans or no popular panels → nothing to smooth
  if (orphanIDs.size === 0) {
    const totalSKUs = countUniqueSKUs(newWallResults, newCeilingResult);
    return {
      wallResults: newWallResults,
      ceilingResult: newCeilingResult,
      substitutions: [],
      summary: createEmptySummary(totalSKUs),
    };
  }

  // Step 6: Attempt substitution for each orphan occurrence
  const substitutions: SKUSubstitution[] = [];
  let orphansEliminated = 0;
  let orphansKept = 0;

  for (const occ of occurrences) {
    if (!orphanIDs.has(occ.item.productID)) continue;

    const candidates = popularByColor.get(occ.item.color) || [];
    const bestSub = findBestSubstitute(occ, candidates, maxWaste);

    if (bestSub) {
      // Look up the substitute product
      const subProduct = getWCBProduct(
        bestSub.candidateLengthFeet as 8 | 10 | 12 | 14 | 16 | 20,
        occ.item.color
      );

      if (subProduct) {
        // Record substitution
        substitutions.push({
          originalSKU: {
            productID: occ.item.productID,
            productNumber: occ.item.productNumber,
            name: occ.item.name,
            lengthFeet: occ.item.lengthFeet,
            quantity: occ.item.quantity,
            color: occ.item.color,
          },
          replacementSKU: {
            productID: subProduct.productID,
            productNumber: subProduct.productNumber,
            name: subProduct.name,
            lengthFeet: subProduct.lengthFeet,
          },
          wasteImpactFeet: bestSub.wasteImpactFeet,
          wasteImpactPercent: bestSub.wasteImpactPercent,
        });

        // Replace the panel in-place in the cloned result
        const replacementItem: MaterialItem = {
          productNumber: subProduct.productNumber,
          productID: subProduct.productID,
          name: subProduct.name,
          color: subProduct.color as ProductColor,
          quantity: bestSub.newQuantity,
          lengthFeet: subProduct.lengthFeet,
          sqft: subProduct.sqft,
          type: occ.item.type, // Preserve original type for PPG section display
        };

        if (occ.source === 'wall') {
          newWallResults[occ.sourceIndex].panels[occ.panelIndex] = replacementItem;
        } else if (occ.source === 'ceiling' && newCeilingResult) {
          newCeilingResult.panels[occ.panelIndex] = replacementItem;
        }

        orphansEliminated++;
      }
    } else {
      orphansKept++;
    }
  }

  // Step 7: Build summary
  const originalSKUCount = globalQty.size;
  const smoothedSKUCount = countUniqueSKUs(newWallResults, newCeilingResult);

  const originalTotalFeet = occurrences.reduce(
    (sum, occ) => sum + occ.item.lengthFeet * occ.item.quantity,
    0
  );
  const totalWasteImpactFeet = substitutions.reduce(
    (sum, s) => sum + s.wasteImpactFeet,
    0
  );
  const totalWasteImpactPercent =
    originalTotalFeet > 0 ? totalWasteImpactFeet / originalTotalFeet : 0;

  return {
    wallResults: newWallResults,
    ceilingResult: newCeilingResult,
    substitutions,
    summary: {
      originalSKUCount,
      smoothedSKUCount,
      orphansEliminated,
      orphansKept,
      totalWasteImpactFeet,
      totalWasteImpactPercent,
    },
  };
}

// ============================================================================
// PANEL OCCURRENCE COLLECTION
// ============================================================================

/**
 * Collect all W&CB panel occurrences from walls and ceiling,
 * annotated with cut-length metadata needed for substitution evaluation.
 */
function collectPanelOccurrences(
  wallResults: WallResult[],
  ceilingResult: CeilingResult | null
): PanelOccurrence[] {
  const occurrences: PanelOccurrence[] = [];

  // Walls
  for (let wi = 0; wi < wallResults.length; wi++) {
    const wall = wallResults[wi];
    for (let pi = 0; pi < wall.panels.length; pi++) {
      const panel = wall.panels[pi];
      const isVertical = panel.type.includes('Vertical');

      let cutLength: number;
      if (isVertical) {
        cutLength = getCutLengthForVerticalPanel(panel.type, wall.coreDimensions);
      } else {
        // Horizontal panels: installed at full stock length, no cutting
        cutLength = panel.lengthFeet;
      }

      const cutsPerPanel = isVertical
        ? Math.floor(panel.lengthFeet / cutLength)
        : 1;

      occurrences.push({
        source: 'wall',
        sourceIndex: wi,
        panelIndex: pi,
        item: panel,
        cutLength,
        totalCutsNeeded: cutsPerPanel * panel.quantity,
        isVertical,
      });
    }
  }

  // Ceiling
  if (ceilingResult) {
    for (let pi = 0; pi < ceilingResult.panels.length; pi++) {
      const panel = ceilingResult.panels[pi];
      occurrences.push({
        source: 'ceiling',
        sourceIndex: 0,
        panelIndex: pi,
        item: panel,
        cutLength: panel.lengthFeet,
        totalCutsNeeded: panel.quantity,
        isVertical: false,
      });
    }
  }

  return occurrences;
}

/**
 * Determine cut length for a vertical panel based on its type tag
 * and the wall's core dimensions.
 *
 * Vertical panels are cut to the section height. The type tag encodes
 * which section the panel belongs to (BelowSW, AboveSW, AboveGarage).
 */
function getCutLengthForVerticalPanel(
  type: string,
  coreDims: CoreDimensions
): number {
  if (type.includes('Below_SW') || type.includes('BelowSW')) {
    return coreDims.wallheightBelowSW;
  }
  if (type.includes('Above_SW') || type.includes('AboveSW')) {
    return coreDims.wallheightAboveSW;
  }
  if (type.includes('Above_Garage') || type.includes('AboveGarage')) {
    return coreDims.heightAboveGarage;
  }
  // Fallback: full room height (single section wall)
  return coreDims.commonRoomHeight;
}

// ============================================================================
// SUBSTITUTION EVALUATION
// ============================================================================

interface PopularPanel {
  productID: string;
  lengthFeet: number;
  color: ProductColor;
  globalQty: number;
}

interface SubstitutionCandidate {
  candidateProductID: string;
  candidateLengthFeet: number;
  newQuantity: number;
  wasteImpactFeet: number;
  wasteImpactPercent: number;
}

/**
 * Find the best popular panel substitute for an orphan occurrence.
 *
 * For VERTICAL panels (cut to section height):
 * - Candidate must have lengthFeet >= cutLength (can make the cuts)
 * - Recalculate quantity based on cuts-per-candidate
 * - Waste = (new total material) - (original total material)
 *
 * For HORIZONTAL panels (installed at full stock length):
 * - Candidate must have lengthFeet >= orphan.lengthFeet
 * - Same quantity (each panel still covers one row position)
 * - Waste = (candidate.length - orphan.length) x quantity
 *
 * @returns Best substitute within waste tolerance, or null
 */
function findBestSubstitute(
  occ: PanelOccurrence,
  candidates: PopularPanel[],
  maxWastePercent: number
): SubstitutionCandidate | null {
  let best: SubstitutionCandidate | null = null;
  let bestWaste = Infinity;

  for (const candidate of candidates) {
    // Same color is guaranteed by caller (candidates filtered by color)

    if (occ.isVertical) {
      // Vertical: candidate must be able to make the cut
      if (candidate.lengthFeet < occ.cutLength) continue;

      const cutsPerCandidate = Math.floor(candidate.lengthFeet / occ.cutLength);
      const newPanelsNeeded = Math.ceil(occ.totalCutsNeeded / cutsPerCandidate);
      const newTotalMaterial = newPanelsNeeded * candidate.lengthFeet;
      const originalTotalMaterial = occ.item.quantity * occ.item.lengthFeet;
      const wasteImpactFeet = newTotalMaterial - originalTotalMaterial;
      const wasteImpactPercent =
        originalTotalMaterial > 0 ? wasteImpactFeet / originalTotalMaterial : 0;

      if (wasteImpactPercent <= maxWastePercent && wasteImpactFeet < bestWaste) {
        best = {
          candidateProductID: candidate.productID,
          candidateLengthFeet: candidate.lengthFeet,
          newQuantity: newPanelsNeeded,
          wasteImpactFeet,
          wasteImpactPercent,
        };
        bestWaste = wasteImpactFeet;
      }
    } else {
      // Horizontal: candidate must be at least as long
      if (candidate.lengthFeet < occ.item.lengthFeet) continue;

      const wasteImpactFeet =
        (candidate.lengthFeet - occ.item.lengthFeet) * occ.item.quantity;
      const originalTotalMaterial = occ.item.quantity * occ.item.lengthFeet;
      const wasteImpactPercent =
        originalTotalMaterial > 0 ? wasteImpactFeet / originalTotalMaterial : 0;

      if (wasteImpactPercent <= maxWastePercent && wasteImpactFeet < bestWaste) {
        best = {
          candidateProductID: candidate.productID,
          candidateLengthFeet: candidate.lengthFeet,
          newQuantity: occ.item.quantity,
          wasteImpactFeet,
          wasteImpactPercent,
        };
        bestWaste = wasteImpactFeet;
      }
    }
  }

  return best;
}

// ============================================================================
// LEGACY BOM-LEVEL SMOOTHING (kept for backward compatibility)
// ============================================================================

/**
 * Apply BOM smoothing to consolidate orphan SKUs.
 *
 * NOTE: This is the legacy BOM-level smoothing function. For new code,
 * prefer `smoothResults()` which operates before surface breakdowns
 * and updates both PPG and BOM.
 *
 * @param bom - Original Bill of Materials
 * @param config - Smoothing configuration
 * @returns Smoothed BOM with substitution details
 */
export function smoothBillOfMaterials(
  bom: BillOfMaterials,
  config: BOMSmoothingConfig
): BOMSmoothingResult {
  if (!config.enabled) {
    return {
      smoothedBOM: bom,
      substitutions: [],
      summary: createEmptySummary(bom.wallAndCeilingBoard.length),
    };
  }

  const { smoothedItems, substitutions, summary } = smoothPanelsByColor(
    bom.wallAndCeilingBoard,
    config
  );

  const smoothedBOM: BillOfMaterials = {
    ...bom,
    wallAndCeilingBoard: smoothedItems,
  };

  return {
    smoothedBOM,
    substitutions,
    summary,
  };
}

// ============================================================================
// LEGACY SMOOTHING INTERNALS
// ============================================================================

interface LegacySmoothingState {
  smoothedItems: MaterialItem[];
  substitutions: SKUSubstitution[];
  summary: BOMSmoothingSummary;
}

function smoothPanelsByColor(
  items: MaterialItem[],
  config: BOMSmoothingConfig
): LegacySmoothingState {
  const originalSKUCount = items.length;
  const allSmoothedItems: MaterialItem[] = [];
  const allSubstitutions: SKUSubstitution[] = [];
  let totalWasteImpactFeet = 0;
  let orphansEliminated = 0;
  let orphansKept = 0;

  const panelsByColor = groupByColor(items);

  for (const [, colorPanels] of panelsByColor.entries()) {
    const result = smoothColorGroup(colorPanels, config);
    allSmoothedItems.push(...result.smoothedItems);
    allSubstitutions.push(...result.substitutions);
    totalWasteImpactFeet += result.wasteImpactFeet;
    orphansEliminated += result.eliminated;
    orphansKept += result.kept;
  }

  const originalTotalFeet = items.reduce(
    (sum, item) => sum + item.lengthFeet * item.quantity,
    0
  );
  const totalWasteImpactPercent =
    originalTotalFeet > 0 ? totalWasteImpactFeet / originalTotalFeet : 0;

  return {
    smoothedItems: allSmoothedItems,
    substitutions: allSubstitutions,
    summary: {
      originalSKUCount,
      smoothedSKUCount: allSmoothedItems.length,
      orphansEliminated,
      orphansKept,
      totalWasteImpactFeet,
      totalWasteImpactPercent,
    },
  };
}

interface ColorGroupResult {
  smoothedItems: MaterialItem[];
  substitutions: SKUSubstitution[];
  wasteImpactFeet: number;
  eliminated: number;
  kept: number;
}

function smoothColorGroup(
  panels: MaterialItem[],
  config: BOMSmoothingConfig
): ColorGroupResult {
  const threshold = config.orphanThreshold ?? DEFAULT_ORPHAN_THRESHOLD;
  const maxWaste = config.maxWasteIncreasePercent ?? DEFAULT_MAX_WASTE_INCREASE_PERCENT;

  const orphans = panels.filter((p) => p.quantity <= threshold);
  const nonOrphans = panels.filter((p) => p.quantity > threshold);

  if (orphans.length === 0 || nonOrphans.length === 0) {
    return {
      smoothedItems: [...panels],
      substitutions: [],
      wasteImpactFeet: 0,
      eliminated: 0,
      kept: orphans.length,
    };
  }

  const workingNonOrphans = nonOrphans.map((p) => ({ ...p }));
  const substitutions: SKUSubstitution[] = [];
  const keptOrphans: MaterialItem[] = [];
  let totalWasteImpactFeet = 0;

  const sortedOrphans = [...orphans].sort((a, b) => a.lengthFeet - b.lengthFeet);

  for (const orphan of sortedOrphans) {
    const substitution = findLegacySubstitute(orphan, workingNonOrphans, maxWaste);

    if (substitution) {
      const substitute = workingNonOrphans.find(
        (p) => p.productID === substitution.replacementSKU.productID
      );
      if (substitute) {
        substitute.quantity += orphan.quantity;
      }
      substitutions.push(substitution);
      totalWasteImpactFeet += substitution.wasteImpactFeet;
    } else {
      keptOrphans.push(orphan);
    }
  }

  const smoothedItems = [...workingNonOrphans, ...keptOrphans];

  return {
    smoothedItems,
    substitutions,
    wasteImpactFeet: totalWasteImpactFeet,
    eliminated: substitutions.length,
    kept: keptOrphans.length,
  };
}

function findLegacySubstitute(
  orphan: MaterialItem,
  candidates: MaterialItem[],
  maxWastePercent: number
): SKUSubstitution | null {
  let bestSubstitute: SKUSubstitution | null = null;
  let minWasteIncrease = Infinity;

  for (const candidate of candidates) {
    if (candidate.lengthFeet < orphan.lengthFeet) continue;

    const wasteImpactFeet =
      (candidate.lengthFeet - orphan.lengthFeet) * orphan.quantity;
    const originalMaterial = orphan.lengthFeet * orphan.quantity;
    const wasteImpactPercent =
      originalMaterial > 0 ? wasteImpactFeet / originalMaterial : 0;

    if (wasteImpactPercent > maxWastePercent) continue;

    if (wasteImpactFeet < minWasteIncrease) {
      minWasteIncrease = wasteImpactFeet;
      bestSubstitute = {
        originalSKU: {
          productID: orphan.productID,
          productNumber: orphan.productNumber,
          name: orphan.name,
          lengthFeet: orphan.lengthFeet,
          quantity: orphan.quantity,
          color: orphan.color,
        },
        replacementSKU: {
          productID: candidate.productID,
          productNumber: candidate.productNumber,
          name: candidate.name,
          lengthFeet: candidate.lengthFeet,
        },
        wasteImpactFeet,
        wasteImpactPercent,
      };
    }
  }

  return bestSubstitute;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function groupByColor(items: MaterialItem[]): Map<ProductColor, MaterialItem[]> {
  const groups = new Map<ProductColor, MaterialItem[]>();

  for (const item of items) {
    const existing = groups.get(item.color);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(item.color, [item]);
    }
  }

  return groups;
}

function createEmptySummary(skuCount: number): BOMSmoothingSummary {
  return {
    originalSKUCount: skuCount,
    smoothedSKUCount: skuCount,
    orphansEliminated: 0,
    orphansKept: 0,
    totalWasteImpactFeet: 0,
    totalWasteImpactPercent: 0,
  };
}

/**
 * Count unique W&CB panel SKUs across all walls and ceiling.
 */
function countUniqueSKUs(
  wallResults: WallResult[],
  ceilingResult: CeilingResult | null
): number {
  const ids = new Set<string>();

  for (const wall of wallResults) {
    for (const panel of wall.panels) {
      ids.add(panel.productID);
    }
  }

  if (ceilingResult) {
    for (const panel of ceilingResult.panels) {
      ids.add(panel.productID);
    }
  }

  return ids.size;
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

/**
 * Create a default smoothing config with specified enabled state.
 */
export function createSmoothingConfig(
  enabled: boolean,
  options?: Partial<BOMSmoothingConfig>
): BOMSmoothingConfig {
  return {
    enabled,
    orphanThreshold: options?.orphanThreshold ?? DEFAULT_ORPHAN_THRESHOLD,
    maxWasteIncreasePercent:
      options?.maxWasteIncreasePercent ?? DEFAULT_MAX_WASTE_INCREASE_PERCENT,
  };
}

export default {
  smoothResults,
  smoothBillOfMaterials,
  createSmoothingConfig,
};
